// chaos：固定領域内に画像のサイズ・位置・重なり順をランダム配置し、
// ページ全体を範囲としてドラッグ移動できるようにする。
// ドラッグ先はコンテナ内に限らずページ全体だが、ビューポート幅・ページ高さ内に
// クランプして余計なスクロールバー（特に横スクロール）が出ないようにする。
// 画面の横幅が変わったときは位置を再計算する（縦だけの変化は無視・デバウンス）。
// クリック拡大は .js-pswp-container（photoswipe.js）に任せ、ドラッグ時は抑止する。
import { intRandom, customRandom } from './_randNum';
import { shuffleArray } from './_shuffleArray';
import { onEnterView } from './_inView';

const config = {
  // ドラッグ操作まわり。タップ拡大との誤判定を避けるためのしきい値と重なり順。
  drag: {
    threshold: 5, // px。この距離を超えて動いたらドラッグ扱いにし、クリック拡大を抑止する。
    zBase: 1000, // ドラッグした画像の z-index 基準値。掴むたびに加算して最前面へ出す。
  },

  // リサイズ再配置まわり。スマホのアドレスバー開閉などで再計算しすぎないための待ち時間。
  resize: {
    debounce: 200, // ms。横幅リサイズが止まってから再配置するまでの待ち時間。
  },

  // 初回表示アニメーション。画像を少し小さく・透明な状態からランダム遅延で表示する。
  enter: {
    scale: 0.92, // 登場時の初期スケール。1 に近いほど拡大感が弱くなる。
    stagger: 600, // ms。各画像の登場遅延の最大値。大きいほどばらけて現れる。
  },

  // 画面幅による切り替え。SCSS の breakpoint(smartphone) と合わせる。
  breakpoint: {
    smartphone: 767, // px。この幅以下では imageSize.spBoost を適用する。
  },

  // 画像サイズ。ratio は chaos コンテナ幅に対する割合（0.6 = 幅60%）。
  // 枚数が増えるほど base / sqrt(枚数) で自動的に小さくし、min/max で極端さを抑える。
  imageSize: {
    base: 0.6, // 全体のサイズ感。大きいほど各画像が大きくなる。
    min: 0.25, // 枚数が多いときでも、1枚の上限をこの割合未満にしない。
    max: 0.7, // 枚数が少ないとき・SP補正後でも、1枚の上限をこの割合以上にしない。
    randomMin: 0.5, // ランダム幅の下限。maxR に対する割合なので、0.5 なら最大幅の半分以上。
    spBoost: 1.1, // スマホ時のサイズ倍率。1 より大きいほどスマホで画像が大きくなる。
  },

  // 初期配置。ランダム性を残しながら、方向バランス・重なり・はみ出し量を制御する。
  position: {
    // スマホ時のchaos度（散らかり具合）。1 で PC と同じ。
    // 大きいほど重なり・はみ出し・角寄りを許して雑然と散らかり、
    // 小さいほど重ならず整然と並ぶ（例: 1.5 = かなり雑然、0.7 = 控えめ）。
    spChaos: 1.5,

    // スマホ時の左右はみ出し。コンテナ幅比・片側（0.12 = 幅の12%まで外に出られる）。
    // 0 で従来どおり（左右はみ出し無し）。あくまで「出られる範囲」で、
    // 出すぎは bleed.penalty の減点が抑えるので、常に最大まで出るわけではない。
    spOverflowXRatio: 0.3,

    overflowRatio: 0.3, // コンテナ高さ比。上下にどれだけはみ出す候補を許すか。
    grid: 3, // 配置スロットの分割数。3 なら 3x3 の9エリアでバランスを取る。
    cellOverlap: 0.25, // セル幅比。隣セルへにじませる量。大きいほど中央の抜けが減る。
    centerMinCount: 3, // この枚数以上なら中央スロットを候補に混ぜ、真ん中のスカスカ感を抑える。
    yShift: -0.06, // コンテナ高さ比。負値で全体を上寄せ、正値で下寄せする。

    // 角方向への寄りをほんの少し抑え、全体のシルエットをやんわり丸くする。
    roundness: {
      diagonalStart: 0.55, // 中心からの正規化距離。x/y ともにここを超えたら角っぽさを評価する。
      penalty: 0.5, // 角っぽい候補への減点。大きいほど四隅を避け、丸いまとまりに近づく。
    },

    // 1枚ごとに試す配置候補数。増やすほど重なりの少ない候補を見つけやすいが計算量も増える。
    candidates: {
      primarySlot: 14, // その画像に割り当てられたスロット内で試す候補数。
      balancedSlots: 9, // 他のバランス用スロットから追加で試す候補数。
      wholeFrame: 7, // スロットに縛られず chaos 枠全体から試す候補数。
    },

    // はみ出し評価。適度なはみ出しは歓迎し、出すぎだけ抑える。
    bleed: {
      freeRatio: 0.1, // 画像面積比。この割合までは「よいはみ出し」として扱う。
      penalty: 1.0, // freeRatio を超えたはみ出しへの減点。大きいほど枠内に戻りやすい。
      reward: 0.14, // freeRatio 内のはみ出しへの加点。大きいほど外へ出やすい。
    },

    // 配置候補のスコア重み。低いスコアの候補が採用される。
    score: {
      overlap: 12, // 既存画像との重なり面積への減点。最優先なので強めにしている。
      crowding: 0.35, // 近くに画像が密集することへの減点。大きいほどばらける。
      noise: 0.0002, // 同点に近い候補をランダムに揺らす量。小さいほど結果が安定する。
    },

    // 3x3 グリッド上の中央セル。centerMinCount 以上のとき中央抜け対策に使う。
    centerSlot: { x: 1, y: 1 },

    // 対角・対向ペア。ここからシャッフルして、上下左右・斜め方向の偏りを抑える。
    slotPairs: [
      [{ x: 0, y: 0 }, { x: 2, y: 2 }], // 左上 ↔ 右下
      [{ x: 2, y: 0 }, { x: 0, y: 2 }], // 右上 ↔ 左下
      [{ x: 1, y: 0 }, { x: 1, y: 2 }], // 上 ↔ 下
      [{ x: 0, y: 1 }, { x: 2, y: 1 }], // 左 ↔ 右
    ],
  },
};

const clamp = (v, min, max) => (max < min ? min : Math.min(max, Math.max(min, v)));
const randomBetween = (min, max, decimalPlaces = 1) => (
  max <= min ? min : customRandom(min, max, decimalPlaces)
);

export function chaos() {
  document.querySelectorAll('.js-chaos').forEach(setup);
}

function createDirectionCycle() {
  const pairs = shuffleArray(config.position.slotPairs.slice());
  const slots = [];
  for (let i = 0; i < pairs.length; i += 1) {
    const pair = pairs[i];
    if (Math.random() < 0.5) {
      slots.push(pair[0], pair[1]);
    } else {
      slots.push(pair[1], pair[0]);
    }
  }
  return slots;
}

function createBalancedSlots(count) {
  const slots = [];
  while (slots.length < count) {
    const remaining = count - slots.length;
    const directionCycle = createDirectionCycle();

    if (remaining >= config.position.centerMinCount) {
      const directionCount = Math.min(remaining - 1, directionCycle.length);
      const slotGroup = [config.position.centerSlot];
      for (let i = 0; i < directionCount; i += 1) {
        slotGroup.push(directionCycle[i]);
      }
      shuffleArray(slotGroup);
      for (let i = 0; i < slotGroup.length; i += 1) {
        slots.push(slotGroup[i]);
      }
      continue;
    }

    for (let i = 0; i < remaining; i += 1) {
      slots.push(directionCycle[i]);
    }
  }
  return slots;
}

function slotAxisValue(slotIndex, areaStart, areaSize, itemSize, min, max) {
  const cellSize = areaSize / config.position.grid;
  const cellOverlap = cellSize * config.position.cellOverlap;
  const cellMin = areaStart + slotIndex * cellSize - cellOverlap;
  const cellMax = areaStart + (slotIndex + 1) * cellSize - itemSize + cellOverlap;
  const value = cellMax >= cellMin
    ? randomBetween(cellMin, cellMax, 1)
    : areaStart + (slotIndex + 0.5) * cellSize - itemSize / 2;
  return clamp(value, min, max);
}

function setRect(rect, left, top, width, height, slot) {
  rect.left = left;
  rect.top = top;
  rect.width = width;
  rect.height = height;
  rect.right = left + width;
  rect.bottom = top + height;
  rect.area = width * height;
  rect.centerX = left + width / 2;
  rect.centerY = top + height / 2;
  rect.slot = slot;
  return rect;
}

function copyRect(target, rect) {
  target.left = rect.left;
  target.top = rect.top;
  target.width = rect.width;
  target.height = rect.height;
  target.right = rect.right;
  target.bottom = rect.bottom;
  target.area = rect.area;
  target.centerX = rect.centerX;
  target.centerY = rect.centerY;
  target.slot = rect.slot;
  return target;
}

function overlapArea(a, b) {
  const w = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const h = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return w * h;
}

function outsideArea(rect, frame) {
  const visibleW = Math.max(0, Math.min(rect.right, frame.right) - Math.max(rect.left, frame.left));
  const visibleH = Math.max(0, Math.min(rect.bottom, frame.bottom) - Math.max(rect.top, frame.top));
  return rect.area - visibleW * visibleH;
}

function crowdingArea(rect, placedRects, frame) {
  if (!placedRects.length || !frame.diagonal) return 0;
  let nearestSq = Infinity;
  for (let i = 0; i < placedRects.length; i += 1) {
    const placed = placedRects[i];
    const dx = rect.centerX - placed.centerX;
    const dy = rect.centerY - placed.centerY;
    nearestSq = Math.min(nearestSq, dx * dx + dy * dy);
  }
  return rect.area * (1 - clamp(Math.sqrt(nearestSq) / frame.diagonal, 0, 1));
}

function roundnessPenalty(rect, frame) {
  const { roundness } = config.position;
  if (!frame.halfWidth || !frame.halfHeight) return 0;

  const x = Math.abs(rect.centerX - frame.centerX) / frame.halfWidth;
  const y = Math.abs(rect.centerY - frame.centerY) / frame.halfHeight;
  const diagonalRange = 1 - roundness.diagonalStart;
  if (diagonalRange <= 0) return 0;

  const diagonalX = clamp((x - roundness.diagonalStart) / diagonalRange, 0, 1);
  const diagonalY = clamp((y - roundness.diagonalStart) / diagonalRange, 0, 1);
  return rect.area * diagonalX * diagonalY * roundness.penalty;
}

function shiftTop(top, rootH, minTop, maxTop) {
  return clamp(top + rootH * config.position.yShift, minTop, maxTop);
}

function candidateFromSlot(rect, slot, itemW, itemH, layoutBox) {
  const { rootW, rootH, overflowLimit, overflowLimitX } = layoutBox;
  const minLeft = -overflowLimitX;
  const maxLeft = Math.max(minLeft, rootW - itemW + overflowLimitX);
  const minTop = -overflowLimit;
  const maxTop = rootH - itemH + overflowLimit;
  const top = maxTop < minTop
    ? (rootH - itemH) / 2
    : slotAxisValue(
      slot.y,
      -overflowLimit,
      rootH + overflowLimit * 2,
      itemH,
      minTop,
      maxTop,
    );

  return setRect(
    rect,
    slotAxisValue(slot.x, -overflowLimitX, rootW + overflowLimitX * 2, itemW, minLeft, maxLeft),
    shiftTop(top, rootH, minTop, maxTop),
    itemW,
    itemH,
    slot,
  );
}

function candidateFromFrame(rect, itemW, itemH, layoutBox) {
  const { rootW, rootH, overflowLimit, overflowLimitX } = layoutBox;
  const minLeft = -overflowLimitX;
  const maxLeft = Math.max(minLeft, rootW - itemW + overflowLimitX);
  const minTop = -overflowLimit;
  const maxTop = Math.max(minTop, rootH - itemH + overflowLimit);
  return setRect(
    rect,
    randomBetween(minLeft, maxLeft, 1),
    shiftTop(randomBetween(minTop, maxTop, 1), rootH, minTop, maxTop),
    itemW,
    itemH,
    null,
  );
}

function scoreCandidate(rect, placedRects, frame) {
  let overlap = 0;
  for (let i = 0; i < placedRects.length; i += 1) {
    overlap += overlapArea(rect, placedRects[i]);
  }
  const outside = outsideArea(rect, frame);
  const { bleed, score } = config.position;
  const welcomeOutside = Math.min(outside, rect.area * bleed.freeRatio);
  const excessiveOutside = outside - welcomeOutside;
  const crowding = crowdingArea(rect, placedRects, frame);
  const roundness = roundnessPenalty(rect, frame);

  // overlap を最優先。適度なはみ出しは歓迎し、角に寄りすぎた候補は少しだけ抑える。
  // chaos度（frame.chaos、スマホ時のみ 1 以外）が高いほど各減点を弱め、
  // はみ出しへの加点を強めて、重なり・はみ出し・角寄りを許す＝散らかる。
  const chaos = frame.chaos || 1;
  return (overlap * score.overlap
    + excessiveOutside * bleed.penalty
    + crowding * score.crowding
    + roundness) / chaos
    - welcomeOutside * bleed.reward * chaos
    + Math.random() * frame.area * score.noise;
}

function createLayoutBox(rootW, rootH, chaos = 1, overflowXRatio = 0) {
  return {
    rootW,
    rootH,
    // chaos度が高いほど上下のはみ出し候補を広げる。
    overflowLimit: rootH * config.position.overflowRatio * chaos,
    // 左右のはみ出し候補範囲（スマホ時のみ > 0）。
    overflowLimitX: rootW * overflowXRatio,
    frame: {
      left: 0,
      top: 0,
      right: rootW,
      bottom: rootH,
      width: rootW,
      height: rootH,
      area: rootW * rootH,
      diagonal: Math.hypot(rootW, rootH),
      centerX: rootW / 2,
      centerY: rootH / 2,
      halfWidth: rootW / 2,
      halfHeight: rootH / 2,
      chaos, // scoreCandidate で参照するchaos度（重なり・角寄りの許容度）
    },
  };
}

function choosePosition(primarySlot, itemW, itemH, layoutBox, placedRects) {
  const { candidates } = config.position;
  const candidate = {};
  const bestRect = {};
  let bestScore = Infinity;

  const testCandidate = (rect) => {
    const candidateScore = scoreCandidate(rect, placedRects, layoutBox.frame);
    if (candidateScore < bestScore) {
      bestScore = candidateScore;
      copyRect(bestRect, rect);
    }
  };

  for (let i = 0; i < candidates.primarySlot; i += 1) {
    testCandidate(candidateFromSlot(candidate, primarySlot, itemW, itemH, layoutBox));
  }

  const balancedSlots = createBalancedSlots(candidates.balancedSlots);
  for (let i = 0; i < balancedSlots.length; i += 1) {
    testCandidate(candidateFromSlot(candidate, balancedSlots[i], itemW, itemH, layoutBox));
  }

  for (let i = 0; i < candidates.wholeFrame; i += 1) {
    testCandidate(candidateFromFrame(candidate, itemW, itemH, layoutBox));
  }

  return bestRect;
}

function setup(root) {
  const itemData = Array.from(root.querySelectorAll('.js-chaos-item'), (item) => {
    const link = item.querySelector('.js-pswp-open');
    const natW = link ? Number(link.dataset.pswpWidth) : 0;
    const natH = link ? Number(link.dataset.pswpHeight) : 0;
    return {
      item,
      link,
      aspectRatio: natW > 0 ? natH / natW : 1,
      slot: null,
      widthRatio: 0,
      itemW: 0,
      itemH: 0,
      area: 0,
    };
  });
  if (!itemData.length) return;

  let topZ = config.drag.zBase;

  // --- 画像サイズの閾値：合計枚数が多いほど1枚あたりを相対的に小さくする ---
  // 散らした画像の総面積がほぼ一定になるイメージ（size ∝ 1/√N）。
  // 上限（閾値）・下限はクランプして、少なすぎ・多すぎでも極端な大きさにならないようにする。
  const { imageSize } = config;
  const count = itemData.length;
  const maxRatio = clamp(
    imageSize.base / Math.sqrt(count),
    imageSize.min,
    imageSize.max,
  ); // 1枚の幅の上限（コンテナ幅比・基準値）
  const minRatio = maxRatio * imageSize.randomMin;
  const layoutOrder = new Array(count);
  const placedRects = [];
  let overflowX = 0; // 現在の左右はみ出し許容量(px)。layout() がスマホ幅のとき設定する。
  // ※スマホ幅では layout() 内でこの比率を imageSize.spBoost 倍して画像を大きくする。

  // chaos のあるページでは、コンテナ・ウィンドウ幅からのはみ出しで横スクロールが
  // 絶対に出ないよう、html（ビューポート）と body の両方の横はみ出しを封じる。
  // 基本は clip（hidden と違いスクロールコンテナ化しないので sticky 等に安全）。
  // clip 非対応の古いブラウザ（iOS 15 以前など）では hidden にフォールバックする。
  // iOS で html だけでは横パンを防げない歴史的挙動があるため、両方に適用する。
  // ドラッグでページ外へ出す動きも従来からクランプで抑えており、思想は同じ。
  const preventOverflowX = (el) => {
    el.style.overflowX = 'clip';
    if (el.style.overflowX !== 'clip') el.style.overflowX = 'hidden'; // clip 非対応時
  };
  preventOverflowX(document.documentElement);
  preventOverflowX(document.body);

  // アイテムの可動範囲（chaos 基準の座標）を、ページ全体に収まるよう算出する。
  // 横はビューポート幅（縦スクロールバー分を除く）、縦はページ全体の高さに収める。
  // 左右は初期配置と同じはみ出し許容量（overflowX）だけ外へ出られるようにし、
  // はみ出して配置された画像を掴んだ瞬間に内側へ飛ばないようにする。
  const boundsFor = (item) => {
    const r = root.getBoundingClientRect();
    const chaosX = r.left + window.scrollX; // chaos のページ内 X
    const chaosY = r.top + window.scrollY;  // chaos のページ内 Y
    const pageW = document.documentElement.clientWidth;
    const pageH = document.documentElement.scrollHeight;
    return {
      minL: -chaosX - overflowX,
      maxL: pageW - item.offsetWidth - chaosX + overflowX,
      minT: -chaosY,
      maxT: pageH - item.offsetHeight - chaosY,
    };
  };

  // --- 配置計算：サイズ・位置・重なり順をランダムに決める ---
  // 初期化時とリサイズ（横幅変化）時に呼ぶ。コンテナ幅を1回だけ読み、あとは書き込みのみ。
  const layout = () => {
    const rootW = root.clientWidth;
    const rootH = root.clientHeight;
    // SP判定もレイアウトビューポート幅で行う（SCSSのmedia queryと一致・ズーム非依存）。
    const isSp = document.documentElement.clientWidth <= config.breakpoint.smartphone;
    // スマホ幅では chaos度（position.spChaos）と左右はみ出し（position.spOverflowXRatio）を適用する。
    overflowX = isSp ? rootW * config.position.spOverflowXRatio : 0;
    const layoutBox = createLayoutBox(
      rootW,
      rootH,
      isSp ? config.position.spChaos : 1,
      isSp ? config.position.spOverflowXRatio : 0,
    );
    // 3x3 の配置スロットを、対向・対角ペア単位でシャッフルして割り当てる。
    // 完全ランダムの散らばりを残しつつ、上下左右・斜め方向の偏りを抑える。
    const positionSlots = createBalancedSlots(count);
    // スマホ幅では画像比率を大きめにする（画面サイズに応じた調整自体はそのまま）。
    // 自動比率 × スマホ補正。上限は imageSize.max でクランプ
    //（横位置を散らす余地を残す）。
    const boost = isSp ? imageSize.spBoost : 1;
    const maxR = Math.min(maxRatio * boost, imageSize.max);
    const minR = Math.min(minRatio * boost, maxR);
    for (let i = 0; i < count; i += 1) {
      const data = itemData[i];
      const widthRatio = customRandom(minR, maxR, 3);
      const itemW = rootW * widthRatio;
      data.slot = positionSlots[i];
      data.widthRatio = widthRatio;
      data.itemW = itemW;
      data.itemH = itemW * data.aspectRatio;
      data.area = data.itemW * data.itemH;
      layoutOrder[i] = data;
    }

    placedRects.length = 0;
    shuffleArray(layoutOrder).sort((a, b) => b.area - a.area);
    for (let i = 0; i < layoutOrder.length; i += 1) {
      const data = layoutOrder[i];
      const rect = choosePosition(data.slot, data.itemW, data.itemH, layoutBox, placedRects);
      data.item.style.width = `${data.widthRatio * 100}%`;
      data.item.style.zIndex = intRandom(1, count);
      data.item.style.left = `${rect.left}px`;
      data.item.style.top = `${rect.top}px`;
      placedRects.push(rect);
    }
  };

  // --- 初期配置 ---
  layout();

  // --- 各アイテムのフェードイン準備とドラッグ設定（初期化時に一度だけ）---
  itemData.forEach(({ item, link }) => {
    // 登場アニメ：ランダムな遅延で「ばらばらに」、少し小さい状態から現れる。
    item.style.setProperty('--fade-delay', `${Math.round(Math.random() * config.enter.stagger)}ms`);
    item.style.opacity = '0';
    item.style.transform = `scale(${config.enter.scale})`;

    // --- ドラッグ移動 ---
    let dragging = false;
    let moved = false;
    let forwardingClick = false;
    let startX = 0, startY = 0, baseLeft = 0, baseTop = 0;
    let bounds = null;

    item.addEventListener('dragstart', (e) => e.preventDefault());

    item.addEventListener('pointerdown', (e) => {
      if (e.button > 0) return; // マウスは左ボタンのみ
      // 新しい操作の開始時に必ずクリック抑止フラグを下ろす。
      moved = false;
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      baseLeft = parseFloat(item.style.left) || 0;
      baseTop = parseFloat(item.style.top) || 0;
      bounds = boundsFor(item); // 掴んだ時点でページ範囲を確定（ドラッグ中はスクロールしない）
      topZ += 1;
      item.style.zIndex = topZ; // 掴んだものをページ最前面へ
      item.classList.add('is-dragging');
      item.setPointerCapture(e.pointerId);
    });

    item.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > config.drag.threshold || Math.abs(dy) > config.drag.threshold) moved = true;
      item.style.left = `${clamp(baseLeft + dx, bounds.minL, bounds.maxL)}px`;
      item.style.top = `${clamp(baseTop + dy, bounds.minT, bounds.maxT)}px`;
    });

    const endDrag = () => {
      dragging = false;
      item.classList.remove('is-dragging');
    };
    item.addEventListener('pointerup', endDrag);
    item.addEventListener('pointercancel', endDrag);

    // ドラッグした場合のクリックは PhotoSwipe 起動を抑止する。純タップ時に
    // pointer capture で click が item に寄った場合は、画像リンクへ渡し直す。
    if (link) {
      item.addEventListener('click', (e) => {
        if (forwardingClick) return;
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
          return;
        }
        if (e.target.closest('.js-pswp-open')) return;
        e.preventDefault();
        e.stopPropagation();
        forwardingClick = true;
        link.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX: e.clientX,
          clientY: e.clientY,
        }));
        forwardingClick = false;
      }, true);
    }
  });

  // 初期状態（opacity:0 / scale）を「トランジション無し」のうちに確定させてから、
  // 登場用トランジション（is-enter）を付ける。順序が逆だと opacity 1→0 自体が
  // フェードしてしまい、表示領域内でリロードしたとき登場フェードが消費される。
  void root.offsetWidth; // 強制リフローで初期状態を確定
  itemData.forEach(({ item }) => item.classList.add('is-enter'));

  // --- リサイズ：レイアウトビューポートの横幅が変わったときだけ位置を再計算する ---
  // 幅の基準は document.documentElement.clientWidth（＝レイアウトビューポート幅）。
  // window.innerWidth と違い、以下では変化しないので余計な再計算を防げる：
  //   - モバイルのアドレスバー表示/非表示（縦だけの変化）
  //   - スマホのピンチズーム（visual viewport だけが変わりレイアウト幅は不変）
  // リサイズ中は連続でイベントが来るため、デバウンスして落ち着いてから一度だけ実行（負荷対策）。
  let lastWidth = document.documentElement.clientWidth;
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    const currentWidth = document.documentElement.clientWidth;
    if (currentWidth === lastWidth) return; // 横幅が変わらないなら何もしない
    lastWidth = currentWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(layout, config.resize.debounce);
  });

  // ビューポートに入ったら、フェード＋拡大（scale → 1）で登場させる。
  // 初期状態は確定済み・is-enter 付与済みなので、ここでの変更が確実にトランジションする。
  onEnterView(root, () => {
    itemData.forEach(({ item }) => {
      item.style.opacity = '1';
      item.style.transform = 'scale(1)';
    });
  });

  root.classList.add('is-init');
}
